/**
 * SegmentCardGrid — gallery of SegmentCards shown in the "cards" view of the
 * Segments tab. Handles empty state and persona slicing per segment.
 */

import { useMemo } from 'react'
import { LayoutList, Plus } from 'lucide-react'
import type { MarketPersona, MarketSegmentWithScore, SegmentPersonaLink } from '@ispot/shared'
import { SegmentCard } from './SegmentCard'

interface Props {
  segments: MarketSegmentWithScore[]
  personas: MarketPersona[]
  coverage: SegmentPersonaLink[]
  beachheadId: string | null
  selectedId?: string | null
  onOpen: (id: string) => void
  onAddClick: () => void
}

export function SegmentCardGrid({
  segments,
  personas,
  coverage,
  beachheadId,
  selectedId,
  onOpen,
  onAddClick,
}: Props) {
  // Index personas once, then slice per segment by the coverage table.
  const personaById = useMemo(() => new Map(personas.map((p) => [p.id, p])), [personas])
  const linksBySegment = useMemo(() => {
    const map = new Map<string, SegmentPersonaLink[]>()
    for (const link of coverage) {
      const bucket = map.get(link.segmentId) ?? []
      bucket.push(link)
      map.set(link.segmentId, bucket)
    }
    return map
  }, [coverage])

  if (segments.length === 0) {
    return <EmptyState onAddClick={onAddClick} />
  }

  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <ul
        className="mx-auto grid max-w-[1440px] list-none gap-5 p-0"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))' }}
      >
        {segments.map((segment) => {
          const links = linksBySegment.get(segment.id) ?? []
          // Coverage-first, then remaining personas. Always preserve order of insertion.
          const linkedPersonas = links
            .map((l) => personaById.get(l.personaId))
            .filter((p): p is MarketPersona => !!p)
          return (
            <li key={segment.id}>
              <SegmentCard
                segment={segment}
                isBeachhead={segment.id === beachheadId}
                selected={segment.id === selectedId}
                personas={linkedPersonas}
                personaCount={linkedPersonas.length}
                onOpen={() => onOpen(segment.id)}
              />
            </li>
          )
        })}
        <li>
          <AddSegmentTile onClick={onAddClick} />
        </li>
      </ul>
    </div>
  )
}

function AddSegmentTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-full min-h-[420px] w-full flex-col items-center justify-center gap-3 rounded-[18px] px-6 py-10 text-center transition-colors hover:bg-[var(--color-warm-100)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent,#e84300)] focus-visible:ring-offset-2"
      style={{ border: '1.5px dashed var(--color-warm-300, #e0dfd7)' }}
    >
      <span
        className="inline-flex h-12 w-12 items-center justify-center rounded-full transition-colors"
        style={{
          backgroundColor: 'var(--color-accent-50, #fdece6)',
          color: 'var(--color-accent, #e84300)',
        }}
      >
        <Plus className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="font-display text-base font-semibold" style={{ color: 'var(--color-text)' }}>
        Add a segment
      </span>
      <span
        className="max-w-[240px] text-[12px] leading-[1.55]"
        style={{ color: 'var(--color-text-muted)' }}
      >
        Skerpa will draft a description, sizing, and scoring from your sources.
      </span>
    </button>
  )
}

function EmptyState({ onAddClick }: { onAddClick: () => void }) {
  return (
    <div className="flex h-full flex-1 items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div
          className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--color-warm-100)' }}
        >
          <LayoutList className="h-6 w-6" style={{ color: 'var(--color-text-muted)' }} />
        </div>
        <h3 className="font-display text-base font-semibold text-[var(--color-text)]">
          No segments yet
        </h3>
        <p
          className="mx-auto mt-1.5 max-w-sm text-xs"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Add your first market segment. Start with the one you know best — you can iterate as
          you learn, and the AI can fill in sizing/scoring later.
        </p>
        <button
          type="button"
          onClick={onAddClick}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--color-market)' }}
        >
          <Plus className="h-3 w-3" />
          Add first segment
        </button>
      </div>
    </div>
  )
}
